import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook personnalisé pour gérer les données de classement Top X
 * 
 * @param {string} endpoint - Endpoint API pour récupérer les données
 * @param {string} category - Catégorie du classement (vehicles, zones, drivers, etc.)
 * @param {string} metric - Métrique à classer (time, count, violations, etc.)
 * @param {number} topX - Nombre d'éléments à récupérer
 * @param {string} period - Période (day/week/month)
 * @param {object} filters - Filtres additionnels
 */
const useTopRanking = ({
  endpoint,
  category,
  metric,
  topX = 5,
  period = 'week',
  filters = {},
} = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);

  // Fonction pour récupérer les données
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Simulation de données pour la démo - À remplacer par un appel API réel
      // const response = await fetch(`${endpoint}?category=${category}&metric=${metric}&top=${topX}&period=${period}`, {
      //   method: 'GET',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(filters),
      // });
      // const result = await response.json();
      // setData(result.data);

      // Données simulées pour démonstration
      const mockData = generateMockData(category, metric, topX, period);
      setData(mockData);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [endpoint, category, metric, topX, period, filtersKey]);

  // Récupérer les données au montage et quand les dépendances changent
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fonction pour rafraîchir manuellement
  const refresh = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    refresh,
  };
};

/**
 * Générateur de données mock pour la démonstration
 * À remplacer par des appels API réels
 */
const generateMockData = (category, metric, topX, period) => {
  const baseNames = {
    vehicles: ['Véhicule LT-001', 'Véhicule LT-002', 'Véhicule LT-003', 'Véhicule CE-001', 'Véhicule CE-002', 'Véhicule OU-001', 'Véhicule OU-002', 'Véhicule LT-004', 'Véhicule CE-003', 'Véhicule OU-003'],
    postes: ['Poste Yopougon', 'Poste Abobo', 'Poste Cocody', 'Poste Adjamé', 'Poste Treichville', 'Poste Marcory', 'Poste Koumassi', 'Poste Port-Bouët'],
    fourrieres: ['Fourrière Abobo', 'Fourrière Yopougon', 'Fourrière Cocody', 'Fourrière Adjamé', 'Fourrière Treichville'],
    ecoles: ['École Primaire A', 'École Primaire B', 'École Primaire C', 'École Primaire D', 'École Primaire E'],
    colleges: ['Collège Moderne A', 'Collège Moderne B', 'Lycée Classique', 'Lycée Technique', 'Collège Moderne C'],
    universites: ['Université de Cocody', 'Université de Yopougon', 'Université Nangui Abrogoua', 'INP-HB', 'ESC'],
    carrefours: ['Carrefour Abobo', 'Carrefour Adjamé', 'Carrefour Cocody', 'Carrefour Yopougon', 'Carrefour Treichville'],
    drivers: ['Jean Dupont', 'Marie Lambert', 'Pierre Martin', 'Sophie Bernard', 'Thomas Leroy', 'Camille Petit', 'Nicolas Roux', 'Laura Michel'],
  };

  const units = {
    time: 'min',
    count: '',
    violations: '',
    alerts: '',
    deviations: '',
    compliance: '%',
  };

  const names = baseNames[category] || baseNames.vehicles;
  const unit = units[metric] || '';

  // Générer des données aléatoires triées
  return Array.from({ length: Math.min(topX, names.length) }, (_, i) => ({
    id: `${category}-${i + 1}`,
    name: names[i],
    value: Math.floor(Math.random() * (100 - i * 10)) + i * 10,
    unit: unit,
    trend: Math.floor(Math.random() * 20) - 10, // Variation entre -10% et +10%
    period: period,
  }));
};

// Hooks spécialisés pour chaque type de classement
export const useTopVehicles = (metric = 'time', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'vehicles',
    metric,
    topX,
    period,
  });
};

export const useTopPostes = (metric = 'count', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'postes',
    metric,
    topX,
    period,
  });
};

export const useTopFourrieres = (metric = 'count', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'fourrieres',
    metric,
    topX,
    period,
  });
};

export const useTopEcoles = (metric = 'count', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'ecoles',
    metric,
    topX,
    period,
  });
};

export const useTopColleges = (metric = 'count', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'colleges',
    metric,
    topX,
    period,
  });
};

export const useTopUniversites = (metric = 'count', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'universites',
    metric,
    topX,
    period,
  });
};

export const useTopCarrefours = (metric = 'count', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'carrefours',
    metric,
    topX,
    period,
  });
};

export const useTopDrivers = (metric = 'time', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'drivers',
    metric,
    topX,
    period,
  });
};

export const useTopViolations = (limitation = '50', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'vehicles',
    metric: 'violations',
    topX,
    period,
    filters: { limitation },
  });
};

export const useTopAlerts = (type = 'panic', topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'vehicles',
    metric: 'alerts',
    topX,
    period,
    filters: { type },
  });
};

export const useTopDeviations = (topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'vehicles',
    metric: 'deviations',
    topX,
    period,
  });
};

export const useTopNonConformite = (topX = 5, period = 'week') => {
  return useTopRanking({
    category: 'vehicles',
    metric: 'compliance',
    topX,
    period,
  });
};

export default useTopRanking;
